import {
  colorContentDim,
  colorContentSubtle,
} from '@duxton-web/design-tokens/themes/light';
import { DatePicker, Flex, Form, Select, Switch, TimePicker, Tooltip, Typography } from '@duxton-web/ui';
import { MoreInfoOutlined } from '@duxton-web/icons';
import type { DatePickerProps } from '@duxton-web/ui';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { showControlGroup } from 'omega-rtc/config';
import {
  DEFAULT_CONTROL_TIME,
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  HUMAN_READ_DATE_FORMAT,
  HUMAN_READ_DATE_TIME_FORMAT,
  SMART_CAMPAIGN_FORM_KEYS,
  SMART_CAMPAIGN_MIN_START_DATE,
} from 'omega-rtc/constants/ucd';
import { getCountryTimezonesFromCountryID } from 'omega-rtc/utils/ucd-utils';
import { formatTimezoneString } from 'omega-rtc/utils/time-utils';
import { useSelector } from 'react-redux';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const MM_TIME_WINDOW_START_UTC_HOUR = 0;
const MM_TIME_WINDOW_END_UTC_HOUR = 13;
const MIN_TIME_WINDOW_HOURS = 2;
const MAX_TIME_WINDOW_HOURS = 4;

interface SmartCampaignMetaDataTimeProps {
  watchProject: string;
  isRecurrenceEnabled: boolean;
  onRecurringCampaignChange: (checked: boolean) => void;
}

const SmartCampaignMetaDataTime: React.FC<SmartCampaignMetaDataTimeProps> = ({
  watchProject,
  isRecurrenceEnabled,
  onRecurringCampaignChange,
}) => {
  const form = Form.useFormInstance();
  const countries = useSelector((state: any) => state.countries.entities);
  const projectView = useSelector((state: any) => state.ucd.projectView);
  // Track previous project to detect actual project changes (not just re-mounts)
  const prevProjectRef = useRef(watchProject);

  const watchStartDate = Form.useWatch(
    SMART_CAMPAIGN_FORM_KEYS.START_DATE,
    form,
  );
  const watchEndDate = Form.useWatch(SMART_CAMPAIGN_FORM_KEYS.END_DATE, form);
  const watchControlGroupTime = Form.useWatch(SMART_CAMPAIGN_FORM_KEYS.CONTROL_GROUP_TIME, form);
  const watchTimeWindow = Form.useWatch(SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW, form);
  const [isTimeSensitive, setIsTimeSensitive] = useState(false);

  const isStartDateValid = useMemo(() => {
    return watchStartDate && watchStartDate.isSameOrAfter(SMART_CAMPAIGN_MIN_START_DATE, 'day');
  }, [watchStartDate]);

  const isEndDateAfterStartDate = useMemo(() => {
    if (!watchStartDate || !watchEndDate) {
      return true;
    }

    return watchEndDate.isSameOrAfter(watchStartDate, 'day');
  }, [watchStartDate, watchEndDate]);

  const isControlGroupValid = useMemo(() => {
    if (!watchStartDate || !watchEndDate || !watchControlGroupTime) {
      return false;
    }

    return (
      watchControlGroupTime.isSameOrAfter(watchStartDate) &&
      watchControlGroupTime.isBefore(watchEndDate)
    );
  }, [watchStartDate, watchEndDate, watchControlGroupTime]);

  const isTimeWindowValid = useMemo(() => {
    if (!watchTimeWindow || !watchTimeWindow[0] || !watchTimeWindow[1]) {
      return true;
    }
    // Validate time window duration
    // Assume the minute of start and end time window are the same
    const startHour = watchTimeWindow[0].hour();
    const endHour = watchTimeWindow[1].hour();
    const diff = endHour - startHour;
    return diff >= MIN_TIME_WINDOW_HOURS && diff <= MAX_TIME_WINDOW_HOURS;
  }, [watchTimeWindow]);

  const isEmptyTimeWindowValid = useMemo(() => {
    if (isTimeSensitive && !(watchTimeWindow && watchTimeWindow[0] && watchTimeWindow[1])) {
      return false;
    }
    return true;
  }, [watchTimeWindow, isTimeSensitive]);

  const handleTimeSensitiveChange = (checked: boolean) => {
    setIsTimeSensitive(checked);
    form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.IS_TIME_SENSITIVE, checked);
    if (!checked) {
      // Reset time window when time sensitive is disabled
      form.setFieldsValue({
        [SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW]: null,
      });
    }
  };
  const { timezoneToBeShown, disabledHours, disabledMinutes } = useMemo(() => {
    let timezoneToBeShown = '';
    const disabledHours: number[] = [];
    const disabledMinutes: number[] = [];
    if (
      !projectView ||
      !projectView.project ||
      projectView.projectID !== watchProject
    ) {
      return { timezoneToBeShown, disabledHours, disabledMinutes };
    }

    const timezones = getCountryTimezonesFromCountryID(
      projectView.projectID === watchProject
        ? projectView.project.countryID
        : 0,
      countries || [],
    );

    if (!timezones.length) {
      return { timezoneToBeShown, disabledHours, disabledMinutes };
    }

    const countryID = projectView.project.countryID;
    const offset = timezones[0].offset;
    timezoneToBeShown = formatTimezoneString(countryID, timezones[0]);
    const offsetHour = Math.floor(offset);

    if (countryID === 9) { // Myanmar, timezone is UTC+6.5
      // Only allow local hours that correspond to 0 to 13 UTC 
      for (let hour = 0; hour < 24; hour += 1) {
        const utcHour = (hour - offsetHour + 24) % 24;
        if (utcHour < MM_TIME_WINDOW_START_UTC_HOUR || utcHour > MM_TIME_WINDOW_END_UTC_HOUR) {
          disabledHours.push(hour);
        }
      }
    }

    // Calculate disabled minutes based on timezone offset, allowed minute is 0 minute in UTC
    const offsetMinute = Math.round((Math.abs(offset) - offsetHour) * 60);
    for (let minute = 0; minute < 60; minute += 1) {
      if (minute !== offsetMinute) {
        disabledMinutes.push(minute);
      }
    }

    return { timezoneToBeShown, disabledHours, disabledMinutes };
  }, [projectView, watchProject, countries]);

  useEffect(() => {
    const fieldsToValidate = [
      SMART_CAMPAIGN_FORM_KEYS.CONTROL_GROUP_TIME,
      SMART_CAMPAIGN_FORM_KEYS.START_DATE,
      SMART_CAMPAIGN_FORM_KEYS.END_DATE,
      SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW,
    ];
    form.validateFields(fieldsToValidate);
  }, [watchStartDate, watchEndDate, watchTimeWindow, isTimeSensitive, form]);

  useEffect(() => {
    if (!showControlGroup && watchStartDate && !watchControlGroupTime) {
      form.setFieldsValue({
        [SMART_CAMPAIGN_FORM_KEYS.CONTROL_GROUP_TIME]: watchStartDate.hour(11).minute(0).second(0),
      });
    }
  }, [watchStartDate, form, watchControlGroupTime]);

  useEffect(() => {
    // Reset time window when project changes, and when it is not duplicated campaign
    if (prevProjectRef.current !== watchProject && prevProjectRef.current !== form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT)) {
      form.setFieldsValue({
        [SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW]: null,
        [SMART_CAMPAIGN_FORM_KEYS.IS_TIME_SENSITIVE]: false,
      });
      setIsTimeSensitive(false);
    }
    prevProjectRef.current = watchProject;
  }, [watchProject, form]);

  useEffect(() => {
    if (form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW) && form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW).length > 0) {
      setIsTimeSensitive(true);
      form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.IS_TIME_SENSITIVE, true);
    }
  }, [form]);

  return (
    <Flex vertical gap={24}>
      {timezoneToBeShown !== '' && (
        <Flex vertical gap={4}>
          <Typography.Body level={2} style={{ color: colorContentSubtle }}>
            Timezone
          </Typography.Body>
          <Typography.Body level={2}>{timezoneToBeShown}</Typography.Body>
        </Flex>
      )}
      <Form.Item
        label="Start date"
        required
        style={{ marginBottom: 0 }}
        name={SMART_CAMPAIGN_FORM_KEYS.START_DATE}
        rules={[
          { message: 'Please input start date', required: true },
          {
            message: 'Start date should be after 2 days from now',
            validator: () => {
              return isStartDateValid
                ? Promise.resolve()
                : Promise.reject('Start date should be after 1 days from now');
            },
          },
        ]}
        initialValue={DEFAULT_START_DATE}
      >
        <DatePickerWrapper
          minDate={SMART_CAMPAIGN_MIN_START_DATE}
          format={HUMAN_READ_DATE_FORMAT}
          disabledDate={(innerDate: Dayjs) =>
            innerDate.endOf('day').isBefore(SMART_CAMPAIGN_MIN_START_DATE)
          }
        />
      </Form.Item>
      <Form.Item
        label="End date"
        required
        style={{ marginBottom: 0 }}
        name={SMART_CAMPAIGN_FORM_KEYS.END_DATE}
        initialValue={DEFAULT_END_DATE}
        rules={[
          { message: 'Please input end date', required: true },
          {
            message: 'End date should be after start date',
            // should be after start date
            validator: () => {
              return isEndDateAfterStartDate
                ? Promise.resolve()
                : Promise.reject('End date should be after start date');
            },
          },
        ]}
      >
        <DatePickerWrapper
          minDate={SMART_CAMPAIGN_MIN_START_DATE}
          format={HUMAN_READ_DATE_FORMAT}
          disabledDate={(innerDate: Dayjs) =>
            (watchStartDate && innerDate.isBefore(watchStartDate)) ||
            !innerDate.endOf('day').isAfter(SMART_CAMPAIGN_MIN_START_DATE)
          }
        />
      </Form.Item>
      <Form.Item
        name={SMART_CAMPAIGN_FORM_KEYS.IS_TIME_SENSITIVE}
        valuePropName="checked"
        style={{ marginBottom: 0 }}
      >
        <Flex align="center" gap={8}>
          <Switch
            checked={isTimeSensitive}
            onChange={(checked) => handleTimeSensitiveChange(checked)}
          />
          <Typography.Body level={2}>Time sensitive</Typography.Body>
          <Tooltip
            title="For example, if this is a food campaign during Ramadan period, it should only show before or after fasting time."
          >
            <MoreInfoOutlined style={{ color: colorContentSubtle, cursor: 'pointer', fontSize: '18px' }} />
          </Tooltip>
        </Flex>
      </Form.Item>
      {isTimeSensitive && (
        <Form.Item
          label="Time window"
          required
          name={SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW}
          style={{ marginBottom: 0 }}
          help={
            isTimeWindowValid && isEmptyTimeWindowValid ? (
              <Typography.Metadata level={1} style={{ color: colorContentDim }}>
                {`Minimum time window is ${MIN_TIME_WINDOW_HOURS} hours and the maximum is ${MAX_TIME_WINDOW_HOURS} hours`}
              </Typography.Metadata>
            ) : null
          }
          rules={[
            {
              message: 'Please enter the time window details.',
              validator: () => {
                return isEmptyTimeWindowValid
                  ? Promise.resolve()
                  : Promise.reject('Please enter the time window details.');
              },
            },
            {
              message: `Please ensure the time duration you've entered is within ${MIN_TIME_WINDOW_HOURS} to ${MAX_TIME_WINDOW_HOURS} hours.`,
              validator: () => {
                return isTimeWindowValid
                  ? Promise.resolve()
                  : Promise.reject(`Please ensure the time duration you've entered is within ${MIN_TIME_WINDOW_HOURS} to ${MAX_TIME_WINDOW_HOURS} hours.`);
              },
            },
          ]}
        >
          <TimePicker.RangePicker
            format="HH:mm"
            style={{ width: '100%' }}
            allowClear
            needConfirm={false}
            value={watchTimeWindow}
            onCalendarChange={(dates) =>
              form.setFieldValue(SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW, dates)
            }
            disabledTime={() => ({
              disabledHours: () => disabledHours,
              disabledMinutes: () => disabledMinutes,
            })}
          />
        </Form.Item>
      )}
      {showControlGroup && (
        <Form.Item
          label="Control Group Trigger Time"
          required
          name={SMART_CAMPAIGN_FORM_KEYS.CONTROL_GROUP_TIME}
          initialValue={DEFAULT_CONTROL_TIME}
          help={
            isControlGroupValid ? (
              <Typography.Metadata level={1} style={{ color: colorContentDim }}>
                If the system cannot personalize the triggering time for each
                user, we will trigger the message at the selected time.
              </Typography.Metadata>
            ) : null
          }
          rules={[
            { message: 'Please select date and time', required: true },
            {
              message: 'The trigger time needs to be within the campaign period',
              // should be between start and end date
              validator: (_, value: Dayjs) => {
                const controlGroupDateTime = dayjs(value);
                if (
                  controlGroupDateTime &&
                  watchStartDate &&
                  controlGroupDateTime.isSameOrAfter(watchStartDate, 'day') &&
                  watchEndDate &&
                  controlGroupDateTime.isSameOrBefore(watchEndDate.endOf('day'))
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  'The trigger time needs to be within the campaign period',
                );
              },
            },
          ]}
        >
          <DatePickerWrapper
            showNow={false}
            format={HUMAN_READ_DATE_TIME_FORMAT}
            showTime={{
              format: 'HH:mm',
            }}
            minDate={SMART_CAMPAIGN_MIN_START_DATE}
            disabledDate={(innerDate: Dayjs) =>
              (watchStartDate && innerDate.isBefore(watchStartDate.startOf('day'))) ||
              (watchEndDate && innerDate.isAfter(watchEndDate.endOf('day'))) || // disable date after end date
              innerDate.isBefore(SMART_CAMPAIGN_MIN_START_DATE, 'day') // disable date before 2 days from now
            }
          />
        </Form.Item>
      )}
      <Form.Item
        name={SMART_CAMPAIGN_FORM_KEYS.IS_RECURRING_CAMPAIGN}
        valuePropName="checked"
        style={{ marginBottom: 0 }}
      >
        <Flex align="center" gap={8}>
          <Switch
            checked={isRecurrenceEnabled}
            onChange={onRecurringCampaignChange}
          />
          <Typography.Body level={2}>Recurring campaign</Typography.Body>
        </Flex>
      </Form.Item>
      {isRecurrenceEnabled && (
        <Flex align="baseline" gap={8}>
          <Typography.Body level={2}>Repeats every</Typography.Body>
          <Form.Item
            name={SMART_CAMPAIGN_FORM_KEYS.RECURRENCE_WINDOW_DAYS}
            style={{ marginBottom: 0 }}
          >
            <Select
              style={{ width: 70 }}
              options={Array.from({ length: 30 }, (_, i) => ({
                label: i + 1,
                value: i + 1,
              }))}
            />
          </Form.Item>
          <Typography.Body level={2}>day(s)</Typography.Body><span style={{ color: '#ff4d4f' }}>*</span>
        </Flex>
      )}
    </Flex>
  );
};

interface DatePickerWrapperProps extends DatePickerProps {
  minDate?: Dayjs;
}

const DatePickerWrapper: React.FC<DatePickerWrapperProps> = (props) => {
  return <DatePicker allowClear={false} style={{ width: '100%' }} {...props} />;
};

export default SmartCampaignMetaDataTime;
